import { clearKeychain, importWallet } from '@plug-core/handlers/walletActions/wallet'
import { GAEventName } from '@plug-core/interfaces/googleAnalytics'
import { logger } from '@plug-core/logger'
import { useGoogleAnalytics } from '@plug-core/providers/GoogleAnalyticsProvider'
import { useCurrentWallet } from '@plug-core/state'
import { setDefaultWalletName } from '@plug-core/utils/setDefaultWalletName'
import { useFindTokensProvider } from '@providers/FindTokensProvider'
import { Banner, Button, Field, Textarea } from '@ui'
import { WALLET_SETUP_ROUTES } from '@utils/walletSetupRoutes'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SetupStep } from './SetupStep'

export const WalletSetupImportSeed = () => {
  const navigate = useNavigate()
  const [phrase, setPhrase] = useState('')
  const [importError, setImportError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { t } = useTranslation()
  const { findTokens } = useFindTokensProvider()
  const { setCurrentWallet } = useCurrentWallet()
  const { fireEvent } = useGoogleAnalytics()

  const onImportWallet = useCallback(async () => {
    setIsLoading(true)

    await clearKeychain()

    importWallet(phrase.trim())
      .then(async (response) => {
        setCurrentWallet(response.principal)
        findTokens(response.principal).catch(logger.debug)
        await setDefaultWalletName(response.principal)
        fireEvent(GAEventName.SIGN_IN, { sign_in_method: 'seedphrase' })
        fireEvent(GAEventName.WALLET_CREATED, { method: 'seedphrase' })
        navigate(WALLET_SETUP_ROUTES.PASSWORD)
      })
      .catch((e) => {
        fireEvent(GAEventName.WALLET_IMPORT_FAILED)
        setImportError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [phrase, setCurrentWallet, findTokens, fireEvent, navigate])

  return (
    <SetupStep title={t('importWallet.title')} description={t('importWallet.description')}>
      <div className="flex flex-col gap-4">
        <Field error={importError}>
          <Textarea
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="none"
            autoComplete="off"
            placeholder={t('importWallet.placeholder')}
            rows={4}
            value={phrase}
            onChange={(e) => {
              setPhrase(e.target.value)
              setImportError(false)
            }}
          />
        </Field>

        {importError && (
          <Banner tone="danger" title={t('importWallet.error')}>
            {t('importWallet.invalidSeed')}
          </Banner>
        )}

        <Button
          variant="primary"
          fullWidth
          disabled={phrase.length === 0 || isLoading}
          onClick={onImportWallet}
          loading={isLoading}
        >
          {t('common.continue')}
        </Button>

        <Button variant="secondary" fullWidth disabled={isLoading} onClick={() => navigate(-1)}>
          {t('common.back')}
        </Button>
      </div>
    </SetupStep>
  )
}
